import { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

const CUST_KEY = "stageletics_token";
const ADMIN_KEY = "stageletics_admin_token";

function authHeader(token) {
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export function AuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem(CUST_KEY));
  const [adminToken, setAdminToken] = useState(() => localStorage.getItem(ADMIN_KEY));
  const [user, setUser] = useState(null);
  const [admin, setAdmin] = useState(null);
  const [ready, setReady] = useState(false);

  // Fetch user/admin identities on token change
  useEffect(() => {
    let cancelled = false;
    async function boot() {
      try {
        if (token) {
          const { data } = await axios.get(`${API}/auth/me`, { headers: authHeader(token) });
          if (!cancelled && data.role === "customer") setUser(data);
          else if (!cancelled) { setUser(null); localStorage.removeItem(CUST_KEY); setToken(null); }
        } else setUser(null);
      } catch {
        if (!cancelled) { setUser(null); localStorage.removeItem(CUST_KEY); setToken(null); }
      }
      try {
        if (adminToken) {
          const { data } = await axios.get(`${API}/auth/me`, { headers: authHeader(adminToken) });
          if (!cancelled && data.role === "admin") setAdmin(data);
          else if (!cancelled) { setAdmin(null); localStorage.removeItem(ADMIN_KEY); setAdminToken(null); }
        } else setAdmin(null);
      } catch {
        if (!cancelled) { setAdmin(null); localStorage.removeItem(ADMIN_KEY); setAdminToken(null); }
      }
      if (!cancelled) setReady(true);
    }
    boot();
    return () => { cancelled = true; };
  }, [token, adminToken]);

  const login = useCallback(async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem(CUST_KEY, data.token);
    setToken(data.token);
    setUser(data.user);
    return data.user;
  }, []);

  const signup = useCallback(async (email, password, name) => {
    const { data } = await axios.post(`${API}/auth/signup`, { email, password, name });
    localStorage.setItem(CUST_KEY, data.token);
    setToken(data.token);
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(CUST_KEY);
    setToken(null);
    setUser(null);
  }, []);

  const adminLogin = useCallback(async (email, password) => {
    const { data } = await axios.post(`${API}/auth/admin/login`, { email, password });
    localStorage.setItem(ADMIN_KEY, data.token);
    setAdminToken(data.token);
    setAdmin(data.user);
    return data.user;
  }, []);

  const adminLogout = useCallback(() => {
    localStorage.removeItem(ADMIN_KEY);
    setAdminToken(null);
    setAdmin(null);
  }, []);

  const forgotPassword = useCallback(async (email) => {
    await axios.post(`${API}/auth/forgot-password`, { email });
  }, []);

  const resetPassword = useCallback(async (token, password) => {
    await axios.post(`${API}/auth/reset-password`, { token, password });
  }, []);

  return (
    <AuthContext.Provider
      value={{
        token, adminToken, user, admin, ready,
        login, signup, logout,
        adminLogin, adminLogout,
        forgotPassword, resetPassword,
        authHeader: () => authHeader(token),
        adminHeader: () => authHeader(adminToken),
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function formatApiError(err) {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}
